/**
 * 内置浏览器（TP / TronLink 等）EVM ↔ Tron 分流：
 * 以钱包当前 eth_chainId / tron 账户为准，避免 localStorage 残留 95500 或 TP 双注入误判。
 */
import { ref } from 'vue'
import { getChainFromId } from './dapp/config.mjs'
import { coerceChainId, db } from './utils.mjs'
import { buildFromChainPageUrl, isTronChainPageUrl, TRON_CHAIN_ID } from './chainNavigation.mjs'
import {
  isTronChainId,
  isTronInjectAvailable,
  readTronAccountPassive,
} from './dapp/tronInject.mjs'

/** '' = 未知/桌面；'evm' | 'tron' = 内置浏览器探测结果 */
export const walletRail = ref('')

let reconcilePromise = null

export function isInAppWalletBrowser(scope) {
  const g = scope || (typeof globalThis !== 'undefined' ? globalThis : {})
  try {
    const ua = String(g.navigator?.userAgent || '')
    return /TokenPocket|TronLink|Bitget|BitKeep|Trust|imToken|;\s*wv\)/i.test(ua)
  } catch (_e) {
    return false
  }
}

export function isTronRailActive() {
  if (walletRail.value === 'evm') return false
  if (walletRail.value === 'tron') return true
  return false
}

export function isEvmRailActive() {
  return walletRail.value === 'evm'
}

/** 探测内置浏览器当前活跃网络（不弹授权窗） */
export async function probeWalletRail(scope) {
  const g = scope || (typeof globalThis !== 'undefined' ? globalThis : {})
  if (!isInAppWalletBrowser(g)) {
    return { rail: null, evmChainId: 0, hasTronAccount: false }
  }

  const eth = g.ethereum || g.bitkeep?.ethereum
  let evmChainId = 0
  let evmAccount = ''

  if (eth?.request) {
    try {
      const hex = await eth.request({ method: 'eth_chainId' })
      evmChainId = coerceChainId(hex, 0)
    } catch (_e) {
      void _e
    }
    try {
      const accs = await eth.request({ method: 'eth_accounts' })
      evmAccount = accs?.[0] ? String(accs[0]).trim() : ''
    } catch (_e) {
      void _e
    }
  }

  const tronPassive = readTronAccountPassive(g)
  const hasTronAccount = !!tronPassive?.account

  // 钱包明确在 EVM 链（BSC 等）：即使 tronWeb 有残留地址也走 EVM
  if (evmChainId > 0 && !isTronChainId(evmChainId) && getChainFromId(evmChainId)) {
    return { rail: 'evm', evmChainId, hasTronAccount }
  }

  // Tron 网络：有 Tron 账户，或仅有 Tron 注入且无有效 EVM chainId
  if (hasTronAccount) {
    return { rail: 'tron', evmChainId: 0, hasTronAccount: true }
  }

  if (isTronInjectAvailable(g) && !evmChainId && !evmAccount) {
    return { rail: 'tron', evmChainId: 0, hasTronAccount: false }
  }

  if (evmAccount && eth) {
    return { rail: 'evm', evmChainId: evmChainId || Number(db.get('chainId', 56)), hasTronAccount }
  }

  return { rail: null, evmChainId, hasTronAccount }
}

function replaceUrlIfNeeded(targetUrl) {
  if (typeof window === 'undefined' || !targetUrl) return
  try {
    if (window.location.href !== targetUrl) {
      window.history.replaceState(window.history.state, '', targetUrl)
    }
  } catch (_e) {
    void _e
  }
}

/**
 * 对齐页面链 / URL 与钱包当前网络，写入 walletRail。
 * @param {{ setChainId?: (n: number) => void, getChainId?: () => number }} hooks
 */
export async function reconcileWalletRail(hooks = {}) {
  const setChainId = hooks.setChainId
  const getChainId = hooks.getChainId || (() => Number(db.get('chainId', 56)))
  const pageChainId = getChainId()
  const fromTronUrl = typeof location !== 'undefined' && isTronChainPageUrl(location.search)

  if (!isInAppWalletBrowser()) {
    walletRail.value = ''
    return { rail: null, evmChainId: 0, hasTronAccount: false }
  }

  const probe = await probeWalletRail()

  if (probe.rail === 'evm') {
    walletRail.value = 'evm'
    const staleTron = isTronChainId(pageChainId) || fromTronUrl
    if (staleTron && setChainId) {
      let next = 56
      if (!isTronChainId(pageChainId) && getChainFromId(pageChainId)) {
        next = pageChainId
      } else if (probe.evmChainId > 0 && getChainFromId(probe.evmChainId)) {
        next = probe.evmChainId
      }
      setChainId(next)
      db.set('chainId', next)
      replaceUrlIfNeeded(buildFromChainPageUrl(next))
    }
    return probe
  }

  if (probe.rail === 'tron') {
    walletRail.value = 'tron'
    const staleEvm = !isTronChainId(pageChainId) && !fromTronUrl
    if (staleEvm && setChainId) {
      setChainId(TRON_CHAIN_ID)
      db.set('chainId', TRON_CHAIN_ID)
      replaceUrlIfNeeded(buildFromChainPageUrl(TRON_CHAIN_ID))
    }
    return probe
  }

  walletRail.value = ''
  return probe
}

export function whenWalletRailReady(hooks = {}) {
  if (!reconcilePromise) {
    reconcilePromise = reconcileWalletRail(hooks)
  }
  return reconcilePromise
}

export function resetWalletRailForTests() {
  walletRail.value = ''
  reconcilePromise = null
}
